<script setup>
import { useAppStore } from '@/stores/app'
import { useDataStore } from '@/stores/data'
import BasePage from './BasePage.vue'
import QrScanner from '../shared/QrScanner.vue'
import { ref } from 'vue'
import { promptModal } from '@/composables/promptModal'

const appStore = useAppStore()
const dataStore = useDataStore()
const { showPrompt } = promptModal()

const isScanning = ref(false)
const defaultId = window.location.hostname === 'localhost' ? '1234' : ''

const scanQr = () => {
	console.warn('scanQr', isScanning.value)
	isScanning.value = true
}

const promptId = async () => {
	let code = await showPrompt('Fyll inn din kode', '', defaultId)

	// If string in code can be converted to a number, prepend with 'digital_demo_'
	if (!isNaN(code) && code.trim() !== '' && code.trim().length < 3) {
		code = 'digital_demo_' + code.trim()
	}

	if (code !== null && code.trim() !== '') {
		handleQrCode(code.trim())
	} else {
		console.log('Prompt cancelled or empty code entered.')
		setTimeout(() => {
			appStore.bottomSheet?.autoResize()
		}, 50)
	}
}

const active = () => {
	setTimeout(() => {
		appStore.bottomSheet?.autoResize('300', false)
	}, 300)
}

const startClick = () => {
	// Track the start click event
	appStore.events.trackStartClick()

	if (appStore.invalidPermissions) {
		appStore.setPage('permissions')
	} else {
		appStore.setPage('intro')
	}
}

const openReadMore = () => {
	kettle.openUrl('https://www.opinion.no/rvu')
}

const handleQrCode = (qrData) => {
	console.warn('handleQrCode', qrData)
	isScanning.value = false
	if (qrData) {
		// If qrData starts with http or https, treat it as a URL, and extract the id from the URL ?id=kogenta_demo_1
		if (qrData.startsWith('http://') || qrData.startsWith('https://')) {
			const url = new URL(qrData)
			qrData = url.searchParams.get('id') || ''
		}
		if (!dataStore.respondentAltId || !dataStore.respondentId || !dataStore.respondentShortId) {
			dataStore.showLoginDialog = true
			dataStore.respondentAltId = qrData
		}
	}
}

const gotoSurveyPage = () => {
	if (!dataStore.surveyReady) {
		dataStore.loadingSurvey = true
		dataStore.currentProcess.process = 'Laster spørreundersøkelse...'
	}
	appStore.bottomSheet?.setHeightFullscreen(600, 'OnboardingPage', true)
	appStore.setPage('survey')

	// Track the survey started event
	appStore.events.trackSurveyStarted()
}
</script>

<template>
	<BasePage name="onboarding" @active="active()" title="Reisevaner">
		<template v-if="!appStore.onboardedPermissions && !appStore.trackingTimeout">
			<h2>Velkommen til Reisevaner!</h2>
			<p class="mb-4">Hensikten med denne appen er å samle viktig informasjon om reisevanene til Norges innbyggere. Ved å delta bidrar du til å forme fremtidens transportløsninger i Norge.</p>
			<p class="mb-6">Dataene brukes til nasjonal og regional transportplanlegging, utvikling av samferdselstiltak og forskning.</p>

			<h2 class="mb-2">Hvordan fungere dette?</h2>
			<ol class="space-y-2">
				<li>Appen samler data automatisk om reisene dine ved hjelp av lokasjon, bevegelse og blåtann.</li>
				<li>Dataene analyseres for å lage en detaljert reisedagbok.</li>
				<li>Du får mulighet til å verifisere og korrigere reisenes detaljer, og svare på enkle spørsmål om reisen.</li>
			</ol>

			<div class="small-logos">
				<div><img class="s2" src="/src/assets/statens_veivesen.svg" /></div>
				<div><img src="/src/assets/bane_nor.svg" /></div>
				<div><img src="/src/assets/jernbanedirektoratet.svg" /></div>
				<div><img src="/src/assets/avinor.svg" /></div>
				<div><img src="/src/assets/nye_veier.svg" /></div>
				<div><img class="s1" src="/src/assets/kystverket.svg" /></div>
				<div><img class="s1" src="/src/assets/opinion_logo_balanced.svg" /></div>
			</div>
		</template>

		<template v-if="!appStore.onboardedPermissions && appStore.trackingTimeout">
			<h2>Innsamling av reisedata er nå avsluttet</h2>
			<p class="mb-4"><strong>I tilfelle du har glemt din deltakelse avslutter vi automatisk all datainnsamling og analyseaktivtet etter 7 dager.</strong></p>
			<p class="mb-6">Dersom du ikke har rukket å verifisere noen av dine reisedager eller ønsker å fortsette å bidra med verdifull innsikt, kan du velge å fortsette deltakelsen.</p>
			<p class="mb-6">Dersom du fortsetter å delta vil analysen igjen avsluttes automatisk etter nye 7 dager, men du kan trekke samtykket når som helst.</p>
		</template>

		<template v-if="appStore.onboardedPermissions">
			<div v-if="!dataStore.respondentShortId">
				<h2>Identifisering med QR-kode</h2>
				<p class="mb-4"><strong>Før vi kan starte, må du skanne QR-koden du har fått, eller fylle inn koden manuelt. Dette knytter deg til undersøkelsen du er invitert til å delta i. </strong></p>
				<p class="mb-6">Når du har identifisert deg med kode, må du svare på første del av undersøkelsen, før appen begynner å analysere reisene dine helt automatisk i bakgrunnen.</p>
				<QrScanner :active="isScanning" :onSuccess="handleQrCode" @scanned="handleQrCode" />
			</div>
			<div v-else>
				<template v-if="dataStore.processingError">
					<h2>Det oppstod en feil</h2>
					<p class="mb-4">Vi klarte ikke å hente eller analysere dataene dine. Vennligst prøv igjen senere.</p>
				</template>
				<template v-else>
					<h2>Du er knyttet til undersøkelsen.</h2>
					<p class="mb-4">Før vi starter den automatiske analysen av reisene dine, vil vi gjerne at du svarer på noen spørsmål om transportvanene dine og litt om deg selv.</p>
					<p class="mb-6">Dette hjelper oss med å forstå reisene dine bedre og sikrer at dataene gir mest mulig verdi.</p>
				</template>
			</div>
		</template>

		<template #footer>
			<template v-if="!appStore.onboardedPermissions || appStore.trackingTimeout">
				<div class="button" @click="openReadMore()">
					<i class="fa-solid fa-up-right-from-square"></i>
					Les mer
				</div>
				<div class="button blue full" @click="startClick()">
					<template v-if="appStore.trackingTimeout">Fortsett å delta</template>
					<template v-else>Kom i gang</template>
					<i class="fas fa-chevron-right"></i>
				</div>
			</template>

			<template v-if="appStore.onboardedPermissions && !appStore.onboardedSurvey">
				<template v-if="!dataStore.respondentShortId">
					<div class="w-full flex gap-6">
						<div class="button green" @click="promptId()">
							<i class="fa-solid fa-calculator"></i>
							Skriv kode
						</div>
						<div class="button blue full" @click="scanQr()"><i class="fa-solid fa-qrcode"></i>Skann QR-kode</div>
					</div>
				</template>
				<template v-else>
					<div class="button blue full" @click="gotoSurveyPage()">
						Start undersøkelsen
						<i class="fas fa-chevron-right"></i>
					</div>
				</template>
			</template>
			<template v-else>
				<template v-if="dataStore.processingError">
					<div class="button full" @click="appStore.startInit(true)"><i class="fas fa-redo"></i> Prøv igjen</div>
				</template>
			</template>
		</template>
	</BasePage>
</template>

<style scoped>
.small-logos {
	margin-left: -1rem;
	margin-right: -1rem;
	margin-top: 2.5rem;
	display: flex;
	flex-wrap: wrap;
	gap: max(0.5rem, 1vw);
	justify-content: center;
	align-items: center;
}

.small-logos > div {
	flex: 0 0 calc(25% - max(0.375rem, 0.75vw));
	/* 4 items per row with gap compensation */
	padding: max(0.5rem, 1vw);
	display: flex;
	align-items: center;
	justify-content: center;
	min-height: max(3.6rem, 4.8vw);
}

.small-logos > div img {
	max-height: max(2.4rem, 3vw);
	max-width: max(4.8rem, 7.2vw);
	width: auto;
	height: auto;
	object-fit: contain;
}

/* Make the last 2 items smaller flex-basis so they center naturally */
.small-logos > div:nth-child(n + 6) {
	flex: 0 0 calc(20% - max(0.25rem, 0.5vw));
}
</style>
